import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authApi, { CommunityScreenerResponse } from '../../services/authApi';
import { useBadge } from '../../context/BadgeContext';
import { useAuth } from '../../context/AuthContext';
import './tool-page.css';
import './CoursesPage.css';
import './ScreenersPage.css';

const ScreenersPage: React.FC = () => {
  const [screeners, setScreeners] = useState<CommunityScreenerResponse[]>([]);
  const [loading, setLoading]     = useState(true);
  const [activeTag, setActiveTag] = useState('All');
  const [search, setSearch]       = useState('');
  const [lockedPopupId, setLockedPopupId] = useState<number | null>(null);
  const { markSeen } = useBadge();
  const { user } = useAuth();
  const isPremium = !!user?.isPremium;
  const navigate = useNavigate();

  useEffect(() => { markSeen('SCREENERS'); }, [markSeen]);

  useEffect(() => {
    authApi.getScreeners()
      .then((res) => setScreeners(res.data ?? []))
      .catch(() => setScreeners([]))
      .finally(() => setLoading(false));
  }, []);

  const allTags = ['All', ...Array.from(new Set(screeners.flatMap((s) => s.tags.map((t) => t.trim()))))];

  const visible = screeners.filter((s) => {
    if (activeTag !== 'All' && s.timeframe !== activeTag && !s.tags.map((t) => t.trim()).includes(activeTag)) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const freeCount = screeners.filter((s) => s.isFree).length;

  return (
    <div className="tool-page">
      <div className="courses-hero">
        <p className="courses-eyebrow">Curated Screeners</p>
        <h1 className="courses-title">Screeners</h1>
        <p className="courses-subtitle">Curated screeners used in our learning — for education, not buy/sell signals.</p>
        <div className="courses-stats">
          <div className="cstat-card">
            <span className="cstat-num">{screeners.length}</span>
            <span className="cstat-label">Screeners</span>
          </div>
          <div className="cstat-card">
            <span className="cstat-num">{freeCount}</span>
            <span className="cstat-label">Free</span>
          </div>
          <div className="cstat-card cstat-card--free">
            <span className="cstat-num">Live</span>
            <span className="cstat-label">Live Data</span>
          </div>
        </div>
      </div>

      <div className="courses-toolbar">
        <div className="courses-search-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input className="courses-search" placeholder="Search screeners..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {allTags.map((t) => (
          <button key={t} className={`courses-filter-btn ${activeTag === t ? 'active' : ''}`} onClick={() => setActiveTag(t)}>{t}</button>
        ))}
      </div>

      {loading ? (
        <div className="screener-grid">
          {[1,2,3,4,5,6].map((i) => <div key={i} className="course-skeleton" style={{ height: 160 }} />)}
        </div>
      ) : (
        <>
          <div className="screener-grid">
            {visible.map((s) => (
              <div key={s.id} className="t-card screener-card">
                <div className="screener-card-head">
                  <span className="screener-name">{s.name}</span>
                  <span className={`pill ${s.isFree ? 'pill-free' : 'pill-pro'}`}>{s.isFree ? 'Free' : 'Pro'}</span>
                </div>
                <div className="screener-tf"><TfIcon />{s.timeframe}</div>
                <p className="screener-desc">{s.description}</p>
                <div className="screener-tags">
                  {s.tags.map((t) => <span key={t} className="pill pill-tag">{t.trim()}</span>)}
                </div>
                {s.chartinkScreenerId && (s.isFree || isPremium) ? (
                  <a
                    href={`https://chartink.com/screener/${s.chartinkScreenerId}`}
                    target="_blank" rel="noopener noreferrer"
                    className="screener-link"
                  >
                    Open Screener <ExtIcon />
                  </a>
                ) : s.chartinkScreenerId && !s.isFree && !isPremium ? (
                  <div className="screener-locked-wrap">
                    <button
                      className="screener-link screener-link--locked-btn"
                      onClick={() => setLockedPopupId(lockedPopupId === s.id ? null : s.id)}
                    >
                      🔒 Open Screener <ExtIcon />
                    </button>
                    {lockedPopupId === s.id && (
                      <div className="screener-locked-popup">
                        <span className="slp-msg">Pro screener — upgrade to unlock access</span>
                        <div className="slp-actions">
                          <a href="/pricing" className="slp-upgrade-btn">Upgrade to Pro ↗</a>
                          <button className="slp-close" onClick={() => setLockedPopupId(null)}>✕</button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="screener-link screener-link--disabled">Screener link coming soon</span>
                )}
                <button
                  className="screener-detail-btn"
                  onClick={() => navigate(`/screeners/${s.id}`)}
                >
                  View Details →
                </button>
              </div>
            ))}
          </div>
          {visible.length === 0 && <div className="empty-state">No screeners match your filter.</div>}
        </>
      )}


    </div>
  );
};

export default ScreenersPage;

function TfIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}
function ExtIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
}
function InfoIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" style={{flexShrink:0,marginTop:2}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
}
